import React, { useMemo } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, ThemeProvider, Typography, createTheme } from '@mui/material';
import { ArrowBack, CheckCircle, ShieldMoonOutlined } from '@mui/icons-material';
import {
  hasAnswer,
  isQuestionVisible,
  isSectionVisible,
  PREVIEW_UNSAFE_RESPONSE_TYPES,
} from '../../../support/assessments';
import AssessmentQuestionInput from '../AssessmentQuestionInput';
import AssessmentResultsSummary from '../results/AssessmentResultsSummary';
import AiEraReportView from '../results/AiEraReportView';
import ImportedWidgetsView from '../../DataImport/ImportedWidgetsView';
import { AssessmentLayoutProps } from './types';

/**
 * A chrome-less, full-page "command-center" layout — the dark, glassmorphic aesthetic ported from
 * the standalone `Workshop-assessment-temp` app so a seeded demo assessment renders as an immersive
 * experience rather than a form in the app shell. It is a self-contained `position: fixed` overlay
 * (it does NOT depend on App.tsx's pathname-derived fullscreen flags, because the layout choice lives
 * on the record, not the URL), and it wraps the shared `AssessmentQuestionInput`s in a MUI dark theme
 * so the same inputs stay legible against the dark field.
 *
 * Selected by `metadata.layout === 'command_center'`.
 */

// Cisco "command-center" palette, matched to the template (cisco-blue #02c8ff on deep navy).
const CISCO_BLUE = '#02c8ff';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: CISCO_BLUE },
    background: { default: '#060d1c', paper: 'rgba(16,31,56,0.72)' },
    text: { primary: '#f1f5fb', secondary: '#9fb3cc' },
  },
  shape: { borderRadius: 10 },
});

const AURORA =
  'radial-gradient(1200px 600px at 15% -10%, rgba(2,200,255,0.18), transparent 60%),' +
  'radial-gradient(1000px 500px at 110% 10%, rgba(120,80,255,0.16), transparent 55%),' +
  'radial-gradient(900px 700px at 50% 120%, rgba(2,200,255,0.10), transparent 60%),' +
  'linear-gradient(180deg, #060d1c 0%, #04091a 100%)';

const glassCard = {
  bgcolor: 'rgba(16,31,56,0.6)',
  border: '1px solid rgba(159,179,204,0.18)',
  borderRadius: 3,
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
} as const;

const CommandCenterLayout: React.FC<AssessmentLayoutProps> = ({
  assessment,
  sections,
  questionsBySection,
  allQuestions,
  answers,
  respondent,
  answerFor,
  onChangeFor,
  submitError,
  submitting,
  submitted,
  result,
  report,
  aiEraReport,
  onSubmit,
  onExit,
  previewMode,
}) => {
  const visibleSections = useMemo(
    () => sections.filter((s) => isSectionVisible(s, answers)),
    [sections, answers]
  );

  // Overall completion across currently-visible questions — the header progress read-out.
  const progress = useMemo(() => {
    let total = 0;
    let done = 0;
    visibleSections.forEach((section) => {
      (questionsBySection[section.id] || [])
        .filter((q) => isQuestionVisible(q, answers))
        .forEach((q) => {
          total += 1;
          if (hasAnswer(answerFor(q))) done += 1;
        });
    });
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [visibleSections, questionsBySection, answers, answerFor]);

  const intro = assessment.metadata?.intro as string | undefined;
  const completion = assessment.metadata?.completion_message as string | undefined;

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (t) => t.zIndex.modal,
          overflowY: 'auto',
          background: AURORA,
          color: '#f1f5fb',
          fontFamily: '"CiscoSans", "Inter", system-ui, sans-serif',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            px: { xs: 2, md: 5 },
            py: 2,
            backdropFilter: 'blur(8px)',
            background: 'linear-gradient(180deg, rgba(6,13,28,0.92), rgba(6,13,28,0.55))',
            borderBottom: '1px solid rgba(159,179,204,0.14)',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
              <ShieldMoonOutlined sx={{ color: CISCO_BLUE }} />
              <Box minWidth={0}>
                <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
                  {assessment.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9fb3cc' }}>
                  {submitted ? 'Assessment complete' : `${progress.done} of ${progress.total} answered`}
                </Typography>
              </Box>
            </Box>
            <Button
              startIcon={<ArrowBack />}
              onClick={onExit}
              sx={{ color: '#9fb3cc', flexShrink: 0 }}
            >
              Exit
            </Button>
          </Box>
          {!submitted && (
            <LinearProgress
              variant="determinate"
              value={progress.pct}
              sx={{
                mt: 1.5,
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(159,179,204,0.15)',
                '& .MuiLinearProgress-bar': { backgroundColor: CISCO_BLUE },
              }}
            />
          )}
        </Box>

        <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
          {submitted ? (
            <Box>
              <Box sx={{ ...glassCard, p: 4, mb: 3, textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 52, color: CISCO_BLUE }} />
                <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
                  Assessment submitted
                </Typography>
                <Typography variant="body2" sx={{ color: '#9fb3cc', mt: 1 }}>
                  {completion || `Your responses to “${assessment.name}” have been recorded.`}
                </Typography>
              </Box>
              {result && <AssessmentResultsSummary result={result} report={report} dark />}
              {aiEraReport && (
                <Box mt={3}>
                  <AiEraReportView
                    report={aiEraReport}
                    config={assessment.metadata?.reportLayout}
                    dark
                  />
                </Box>
              )}
              {allQuestions
                .filter((q) => q.response_type === 'data_import')
                .map((q) => answerFor(q)?.datasetId)
                .filter((id): id is number => typeof id === 'number')
                .map((datasetId) => (
                  <Box sx={{ ...glassCard, p: 3, mt: 3 }} key={datasetId}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                      Imported data
                    </Typography>
                    <ImportedWidgetsView datasetId={datasetId} />
                  </Box>
                ))}
              <Box display="flex" justifyContent="center" mt={4}>
                <Button variant="contained" onClick={onExit}>
                  Done
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {!!intro && (
                <Box sx={{ ...glassCard, p: 3, mb: 3 }}>
                  <Typography variant="body1" sx={{ color: '#cdd9ea' }}>
                    {intro}
                  </Typography>
                </Box>
              )}

              {visibleSections.map((section, index) => {
                const questions = (questionsBySection[section.id] || []).filter((q) =>
                  isQuestionVisible(q, answers)
                );
                if (questions.length === 0) return null;

                return (
                  <Box key={section.id} sx={{ ...glassCard, p: { xs: 2.5, md: 4 }, mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                      <Chip
                        size="small"
                        label={`0${index + 1}`.slice(-2)}
                        sx={{
                          bgcolor: 'rgba(2,200,255,0.15)',
                          color: CISCO_BLUE,
                          fontWeight: 700,
                          border: `1px solid ${CISCO_BLUE}55`,
                        }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {section.title}
                      </Typography>
                    </Box>
                    {!!section.description && (
                      <Typography variant="body2" sx={{ color: '#9fb3cc', mb: 2 }}>
                        {section.description}
                      </Typography>
                    )}

                    {questions.map((question) => (
                      <Box key={question.id} mb={3}>
                        <Typography variant="subtitle2" component="label" sx={{ color: '#e6edf6' }}>
                          {question.text}
                          {question.required && (
                            <Typography component="span" sx={{ color: '#ff6b7d', ml: 0.5 }}>
                              *
                            </Typography>
                          )}
                        </Typography>
                        {!!question.help_text && (
                          <Typography variant="caption" sx={{ color: '#8ea3bd', display: 'block', mb: 1 }}>
                            {question.help_text}
                          </Typography>
                        )}
                        <Box mt={1}>
                          <AssessmentQuestionInput
                            question={question}
                            value={answerFor(question)}
                            onChange={onChangeFor(question)}
                            // In an admin preview, disable inputs that would otherwise
                            // fire real side effects (uploads, Salesforce, data import).
                            disabled={!!previewMode && PREVIEW_UNSAFE_RESPONSE_TYPES.includes(question.response_type)}
                            // Only the coupled `sf_account_file` input needs the full (per-keystroke)
                            // answers map; passing it to every input would defeat the memoization.
                            answersByKey={
                              question.response_type === 'sf_account_file' ? answers : undefined
                            }
                            questions={
                              question.response_type === 'sf_account_file' ? allQuestions : undefined
                            }
                            respondent={respondent}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                );
              })}

              {!!submitError && (
                <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              )}

              <Box display="flex" justifyContent="flex-end" gap={2} pb={4}>
                <Button onClick={onExit} disabled={submitting} sx={{ color: '#9fb3cc' }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={onSubmit}
                  disabled={submitting}
                  sx={{ px: 4, fontWeight: 700 }}
                >
                  {submitting ? 'Submitting…' : 'Submit assessment'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default CommandCenterLayout;
